// pages/tasks/TaskChatPanel.jsx
//
// Shared "chat" drawer reused across every non-Technician page (Developer/
// Data-Analyst My Tickets + Complaints, Staff Complaints). This is NOT a
// second chat system: it stores messages in the exact same task_remarks
// table Technician's own TaskManagementPage.js chat already uses (tagged
// remark_type='chat' -- a code-level addition only, no schema change) --
// just reached through dedicated, participant-authorized endpoints:
//   GET  /tasks/:id/chat
//   POST /tasks/:id/chat
// Resource authorization is enforced SERVER-SIDE
// (controllers/taskController.js#resolveTaskChatAccess): a caller may read
// AND send when they are the task's creator, its assignee, an existing
// active task_performers/task_delegations participant, or when their own
// resolved tasks:view scope already covers it (Admin/Manager's existing
// oversight scope) -- deliberately NOT gated by tasks:add_remarks, so
// Developer/Data-Analyst (never granted that permission) can still chat on
// their own created-but-unassigned ticket. Read and send share the SAME
// access check, so there is no separate "read-only" mode here -- if the
// drawer opened, sending is authorized too.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPaperPlane, FaRegCommentDots, FaRegSmile } from "react-icons/fa";
import api from "../../api";
import { taskDisplayTitle } from "../../utils/taskDisplay";

const POLL_MS = 2500;

// A small curated set, not a full emoji library -- kept workplace/support
// appropriate (this is an IT-ticket chat, not a social app) rather than
// pulling in a third-party emoji-picker dependency for one popover.
const EMOJI_CHOICES = [
  "😀", "🙂", "😊", "😂", "😅", "🤔", "😮", "😢",
  "👍", "👎", "🙏", "👏", "💪", "🤝", "👀", "✅",
  "❌", "⚠️", "🔧", "💻", "📌", "📎", "⏰", "🚀",
  "🔥", "💡", "🎉", "❤️", "😡", "🙌", "✔️", "🤞",
];

// Visual only -- a small, fixed palette so each distinct sender in a thread
// gets a consistent avatar color across renders/polls (hashed off their
// name, never off anything sent to the backend).
const AVATAR_PALETTE = ["#6d1530", "#c89a3f", "#0f766e", "#2563eb", "#7c3aed", "#b9852c"];
function avatarColorFor(name) {
  const str = String(name || "?");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}
function initialFor(name) {
  const trimmed = String(name || "?").trim();
  return trimmed ? trimmed[0].toUpperCase() : "?";
}

// A message that's ONLY a handful of emoji (no other text) renders bigger
// and background-free, same convention WhatsApp/iMessage/Slack use -- purely
// cosmetic, never affects what's stored/sent.
const EMOJI_ONLY_RE = /^[\p{Extended_Pictographic}‍️\s]+$/u;
function isEmojiOnlyMessage(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return false;
  if (Array.from(trimmed).length > 12) return false;
  return EMOJI_ONLY_RE.test(trimmed);
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayLabelFor(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(d, today)) return "Today";
  if (isSameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
}

function normalizeRemarks(taskData, currentUserId) {
  const list = Array.isArray(taskData?.remarks) ? taskData.remarks : [];
  return list.map((remark) => {
    const createdBy = remark.created_by || {};
    const fromId = createdBy.id || remark.created_by_id || remark.created_by || remark.user_id || null;
    return {
      id: remark.id,
      text: remark.remark || "",
      from: createdBy.name || remark.created_by_name || "Unknown",
      fromId,
      time: remark.created_at,
      mine: String(fromId) === String(currentUserId),
    };
  });
}

// Day separators now carry the date, so each bubble only needs the time.
function fmtChatTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return value;
  }
}

export default function TaskChatPanel({ taskId, show, onClose, currentUserId, onSent }) {
  const [task, setTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const pollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const emojiRef = useRef(null);

  // Grouping is purely visual (avatar/sender name + a "Today"/"Yesterday"/
  // date divider shown once per run) -- never changes what's sent/read,
  // just how it's displayed.
  const groupedMessages = useMemo(
    () =>
      messages.map((msg, i) => {
        const prev = messages[i - 1];
        const prevDate = prev?.time ? new Date(prev.time) : null;
        const thisDate = msg.time ? new Date(msg.time) : null;
        const isNewDay = i === 0 || !prevDate || !thisDate || !isSameDay(prevDate, thisDate);
        return {
          ...msg,
          isGroupStart: isNewDay || prev.fromId !== msg.fromId,
          isNewDay,
        };
      }),
    [messages]
  );

  useEffect(() => {
    // jsdom (this app's test environment) doesn't implement scrollIntoView
    // at all -- optional-chain the call itself, not just the ref, so tests
    // never need to stub it out.
    messagesEndRef.current?.scrollIntoView?.({ block: "end" });
  }, [groupedMessages.length]);

  // Close the emoji popover on an outside click or Escape -- standard
  // popover behavior, not tied to the chat/send logic at all.
  useEffect(() => {
    if (!showEmoji) return undefined;
    const onDocClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowEmoji(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showEmoji]);

  const fetchThread = async () => {
    if (!taskId) return;
    try {
      const { data } = await api.get(`/tasks/${taskId}/chat`);
      setTask(data || null);
      setMessages(normalizeRemarks(data, currentUserId));
      setNotFound(false);
    } catch (err) {
      // Deliberately generic: the backend returns the SAME 404 whether the
      // task doesn't exist or the caller simply isn't a participant on it,
      // so this drawer must not guess or leak which case it is.
      setTask(null);
      setMessages([]);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !taskId) return;
    setLoading(true);
    fetchThread();
    pollRef.current = setInterval(fetchThread, POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, taskId, currentUserId]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !taskId || sending || notFound) return;
    try {
      setSending(true);
      setInput("");
      await api.post(`/tasks/${taskId}/chat`, { remark: text });
      await fetchThread();
      onSent?.();
    } catch (err) {
      console.error("Send chat message error:", err);
    } finally {
      setSending(false);
    }
  };

  // Inserts at the current cursor position (not just appended at the end),
  // then restores focus + cursor right after the inserted emoji so picking
  // several in a row feels natural.
  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? input.length;
    const end = el?.selectionEnd ?? input.length;
    const next = input.slice(0, start) + emoji + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      el?.focus();
      const cursor = start + emoji.length;
      el?.setSelectionRange?.(cursor, cursor);
    });
  };

  if (!show) return null;

  return (
    <div
      className="tcp-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="tcp-panel">
        <div className="tcp-header">
          <div className="tcp-header-icon"><FaRegCommentDots aria-hidden="true" /></div>
          <div className="tcp-header-text">
            <h5>{task && taskDisplayTitle(task) ? `#${task.id} — ${taskDisplayTitle(task)}` : "Task Chat"}</h5>
            {task?.status && <span className="tcp-status">{task.status}</span>}
          </div>
          <button className="tcp-close" onClick={onClose} aria-label="Close chat">×</button>
        </div>

        <div className="tcp-messages">
          {loading ? (
            <div className="tcp-empty"><FaRegCommentDots aria-hidden="true" /><p>Loading conversation…</p></div>
          ) : notFound ? (
            <div className="tcp-empty">
              <FaRegCommentDots aria-hidden="true" />
              <p>This conversation isn't available. It may not exist, or you may not be a participant on it.</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="tcp-empty"><FaRegCommentDots aria-hidden="true" /><p>No messages yet. Start the conversation on this task.</p></div>
          ) : (
            <>
              {groupedMessages.map((msg) => (
                <React.Fragment key={msg.id}>
                  {msg.isNewDay && <div className="tcp-day-sep"><span>{dayLabelFor(msg.time)}</span></div>}
                  <div className={`tcp-msg ${msg.mine ? "mine" : "theirs"}${msg.isGroupStart ? "" : " grouped"}`}>
                    {!msg.mine && (
                      <div className="tcp-avatar" style={{ background: avatarColorFor(msg.from) }}>
                        {msg.isGroupStart ? initialFor(msg.from) : ""}
                      </div>
                    )}
                    <div className="tcp-msg-body">
                      {!msg.mine && msg.isGroupStart && <div className="tcp-sender">{msg.from}</div>}
                      <div className={`tcp-bubble${isEmojiOnlyMessage(msg.text) ? " tcp-bubble--emoji" : ""}`}>{msg.text}</div>
                      <div className="tcp-time">{fmtChatTime(msg.time)}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="tcp-input-area">
          <div className="tcp-emoji-wrap" ref={emojiRef}>
            <button
              type="button"
              className={`tcp-emoji-btn${showEmoji ? " active" : ""}`}
              onClick={() => setShowEmoji((v) => !v)}
              disabled={notFound || loading}
              aria-label="Add emoji"
              aria-expanded={showEmoji}
            >
              <FaRegSmile aria-hidden="true" />
            </button>
            {showEmoji && (
              <div className="tcp-emoji-popover" role="menu">
                {EMOJI_CHOICES.map((emoji) => (
                  <button
                    type="button"
                    key={emoji}
                    className="tcp-emoji-option"
                    role="menuitem"
                    aria-label={`Insert ${emoji}`}
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            className="tcp-input"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            disabled={notFound || loading}
          />
          <button
            className="tcp-send"
            disabled={!input.trim() || sending || notFound || loading}
            onClick={sendMessage}
            aria-label="Send message"
          >
            {sending ? <span className="tcp-send-spinner" /> : <FaPaperPlane aria-hidden="true" />}
            Send
          </button>
        </div>
      </div>

      <style>{`
        .tcp-overlay{position:fixed;inset:0;background:rgba(74,14,34,.32);backdrop-filter:blur(2px);display:flex;justify-content:flex-end;z-index:1050;animation:tcp-fade .18s ease;}
        @keyframes tcp-fade{from{opacity:0;}to{opacity:1;}}
        .tcp-panel{width:100%;max-width:420px;height:100%;background:var(--surface,#fff);display:flex;flex-direction:column;box-shadow:var(--shadow-lg, -8px 0 24px rgba(15,23,42,.18));animation:tcp-slide-in .22s var(--ease, cubic-bezier(.4,0,.2,1));}
        @keyframes tcp-slide-in{from{transform:translateX(24px);opacity:0;}to{transform:translateX(0);opacity:1;}}

        .tcp-header{display:flex;align-items:center;gap:12px;padding:16px 18px;
          background:linear-gradient(135deg, var(--brand-maroon-dark,#4a0e22) 0%, var(--brand-maroon,#6d1530) 100%);
          color:#fff;flex-shrink:0;}
        .tcp-header-icon{width:36px;height:36px;border-radius:var(--radius-full,999px);background:rgba(255,255,255,.14);
          display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0;}
        .tcp-header-text{flex:1;min-width:0;}
        .tcp-header h5{margin:0;font-size:14px;font-weight:800;letter-spacing:.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .tcp-status{display:inline-block;margin-top:3px;font-size:10px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;
          color:var(--brand-maroon-dark,#4a0e22);background:var(--brand-gold,#c89a3f);border-radius:var(--radius-full,999px);padding:2px 9px;}
        .tcp-close{border:none;background:rgba(255,255,255,.12);width:28px;height:28px;border-radius:var(--radius-full,999px);
          font-size:17px;line-height:1;cursor:pointer;color:#fff;flex-shrink:0;transition:background .15s var(--ease,ease);}
        .tcp-close:hover{background:rgba(255,255,255,.24);}

        .tcp-messages{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:2px;
          background:var(--page-bg, var(--brand-cream, #faf7f1));}
        .tcp-messages::-webkit-scrollbar{width:6px;}
        .tcp-messages::-webkit-scrollbar-thumb{background:rgba(74,14,34,.18);border-radius:999px;}

        .tcp-empty{text-align:center;color:var(--text-muted,#8b877f);font-size:12.5px;margin:auto;display:flex;flex-direction:column;
          align-items:center;gap:10px;padding:24px;}
        .tcp-empty svg{font-size:26px;color:var(--border-strong,#d6c8ae);}
        .tcp-empty p{margin:0;max-width:240px;}

        .tcp-day-sep{display:flex;align-items:center;justify-content:center;margin:16px 0 6px;}
        .tcp-day-sep span{font-size:10.5px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;
          color:var(--brand-maroon,#6d1530);background:var(--brand-maroon-soft,#fbeef1);
          border-radius:var(--radius-full,999px);padding:4px 12px;}

        .tcp-msg{max-width:82%;display:flex;align-items:flex-end;gap:8px;margin-top:14px;animation:tcp-msg-in .16s var(--ease,ease);}
        .tcp-msg.grouped{margin-top:3px;}
        @keyframes tcp-msg-in{from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:translateY(0);}}
        .tcp-msg.mine{align-self:flex-end;flex-direction:row-reverse;}
        .tcp-msg.theirs{align-self:flex-start;}
        .tcp-msg-body{min-width:0;}

        .tcp-avatar{width:26px;height:26px;border-radius:var(--radius-full,999px);flex-shrink:0;display:flex;align-items:center;
          justify-content:center;color:#fff;font-size:11px;font-weight:800;}
        .tcp-msg.theirs.grouped .tcp-avatar{visibility:hidden;}

        .tcp-sender{font-size:11px;font-weight:800;color:var(--brand-maroon,#6d1530);margin-bottom:3px;}
        .tcp-bubble{padding:9px 13px;border-radius:var(--radius-lg,14px);font-size:13.5px;line-height:1.4;
          background:#fff;color:var(--text-primary,#241f1a);box-shadow:var(--shadow-xs, 0 1px 2px rgba(74,14,34,.05));
          border-bottom-left-radius:4px;word-break:break-word;}
        .tcp-msg.mine .tcp-bubble{
          background:linear-gradient(135deg, var(--brand-maroon,#6d1530) 0%, var(--brand-maroon-dark,#4a0e22) 100%);
          color:#fff;border-bottom-left-radius:var(--radius-lg,14px);border-bottom-right-radius:4px;}
        .tcp-msg.theirs .tcp-bubble{border-top-left-radius:4px;}
        .tcp-msg.theirs.grouped .tcp-bubble{border-top-left-radius:var(--radius-lg,14px);}
        .tcp-bubble--emoji{background:none!important;box-shadow:none!important;padding:2px 2px 6px;font-size:34px;line-height:1.1;
          border-radius:0!important;}
        .tcp-time{font-size:9.5px;color:var(--text-muted,#8b877f);margin-top:3px;}
        .tcp-msg.mine .tcp-time{text-align:right;}

        .tcp-input-area{display:flex;align-items:center;gap:8px;padding:12px 14px;border-top:1px solid var(--border,#eef2ff);
          background:var(--surface,#fff);flex-shrink:0;position:relative;}
        .tcp-emoji-wrap{position:relative;flex-shrink:0;}
        .tcp-emoji-btn{width:40px;height:40px;border-radius:var(--radius-full,999px);border:1px solid var(--border,#e5e7eb);
          background:var(--surface,#fff);color:var(--text-muted,#8b877f);font-size:17px;display:flex;align-items:center;
          justify-content:center;cursor:pointer;transition:background .15s var(--ease,ease), color .15s var(--ease,ease), border-color .15s var(--ease,ease);}
        .tcp-emoji-btn:hover:not(:disabled),.tcp-emoji-btn.active{background:var(--brand-maroon-soft,#fbeef1);
          color:var(--brand-maroon,#6d1530);border-color:var(--brand-maroon-soft,#fbeef1);}
        .tcp-emoji-btn:disabled{opacity:.45;cursor:not-allowed;}
        .tcp-emoji-popover{position:absolute;bottom:calc(100% + 10px);left:0;width:236px;background:var(--surface,#fff);
          border:1px solid var(--border,#eef2ff);border-radius:var(--radius-lg,14px);box-shadow:var(--shadow-lg, 0 22px 52px rgba(15,23,42,.16));
          padding:8px;display:grid;grid-template-columns:repeat(8,1fr);gap:2px;z-index:5;animation:tcp-pop-in .14s var(--ease,ease);}
        @keyframes tcp-pop-in{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
        .tcp-emoji-option{width:26px;height:26px;border:none;background:none;border-radius:var(--radius-sm,6px);
          font-size:16px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;
          transition:background .12s var(--ease,ease), transform .12s var(--ease,ease);}
        .tcp-emoji-option:hover{background:var(--brand-cream, #faf7f1);transform:scale(1.15);}
        .tcp-input{flex:1;height:40px;border:1px solid var(--border,#e5e7eb);border-radius:var(--radius-full,999px);padding:0 16px;
          font-size:13.5px;outline:none;transition:border-color .15s var(--ease,ease), box-shadow .15s var(--ease,ease);}
        .tcp-input:focus{border-color:var(--brand-maroon, #6d1530);box-shadow:0 0 0 3px rgba(109,21,48,.10);}
        .tcp-input:disabled{background:#f8fafc;cursor:not-allowed;}
        .tcp-send{border:none;height:40px;border-radius:var(--radius-full,999px);padding:0 18px;font-size:12.5px;font-weight:800;
          display:inline-flex;align-items:center;gap:7px;flex-shrink:0;
          background:linear-gradient(135deg, var(--brand-maroon,#6d1530) 0%, var(--brand-maroon-dark,#4a0e22) 100%);
          color:#fff;cursor:pointer;transition:transform .12s var(--ease,ease), box-shadow .12s var(--ease,ease);
          box-shadow:var(--shadow-sm, 0 2px 10px rgba(74,14,34,.06));}
        .tcp-send:hover:not(:disabled){transform:translateY(-1px);box-shadow:var(--shadow-md, 0 10px 28px rgba(74,14,34,.10));}
        .tcp-send:disabled{opacity:.45;cursor:not-allowed;transform:none;box-shadow:none;}
        .tcp-send-spinner{width:12px;height:12px;border-radius:999px;border:2px solid rgba(255,255,255,.4);
          border-top-color:#fff;animation:tcp-spin .6s linear infinite;}
        @keyframes tcp-spin{to{transform:rotate(360deg);}}
      `}</style>
    </div>
  );
}
